// @ts-nocheck
/**
 * ドロップシステムフック
 * @module twinKey/hooks/useDrops
 */

import { useCallback, useMemo } from 'react';
import { useTwinKeyContext } from '../context/TwinKeyContext';
import { DropManager } from '../content/drops';
import { rewardCalculator } from '../content/drops/rewardCalculator';

/**
 * ドロップシステムフック
 * @returns {Object} ドロップ操作API
 */
export function useDrops() {
  const { state, playerData, updatePlayerData } = useTwinKeyContext();

  // DropManagerインスタンス
  const dropManager = useMemo(() => new DropManager(), []);

  /**
   * 戦闘報酬を計算
   * @param {Object} params - パラメータ
   * @param {string} params.continentId - 大陸ID
   * @param {number} params.floor - 現在階層
   * @param {number} params.maxFloor - 最大階層
   * @param {string} params.battleType - バトルタイプ ('battle', 'strong_enemy', 'boss')
   * @param {Array} params.enemies - 敵配列
   * @returns {Object} 報酬オブジェクト
   */
  const calculateBattleRewards = useCallback((params) => {
    const { continentId, floor, maxFloor, battleType, enemies } = params;

    // 深度を計算（0-1の正規化値）
    const depthRatio = floor / Math.max(1, maxFloor);

    // rewardCalculatorを使用して基本報酬を計算
    const baseReward = rewardCalculator.calculateFloorReward({
      continentId: continentId || 'flame_kingdom',
      floor,
      maxFloor,
      floorType: battleType,
    });

    // ボスの場合は追加報酬
    let drops = [];
    if (battleType === 'boss') {
      const bossDrops = dropManager.rollBossDrops(
        { continentId, depth: depthRatio },
        enemies?.[0] || { name: 'ボス', level: floor * 2 }
      );
      drops = bossDrops.items || [];
    } else {
      // 通常戦闘のドロップ
      const floorDrops = dropManager.rollFloorDrops(
        { continentId, depth: depthRatio },
        floor,
        battleType === 'strong_enemy' ? 1.5 : 1.0
      );
      drops = floorDrops.items || [];
    }

    return {
      gold: baseReward.gold,
      exp: baseReward.exp,
      materials: drops,
    };
  }, [dropManager]);

  /**
   * 宝箱報酬を計算
   * @param {Object} params - パラメータ
   * @param {string} params.continentId - 大陸ID
   * @param {number} params.floor - 現在階層
   * @param {number} params.maxFloor - 最大階層
   * @param {string} params.chestType - 宝箱タイプ ('normal', 'rare', 'legendary')
   * @returns {Object} 報酬オブジェクト
   */
  const calculateTreasureRewards = useCallback((params) => {
    const { continentId, floor, maxFloor, chestType = 'normal' } = params;

    const depthRatio = floor / Math.max(1, maxFloor);

    const treasureDrops = dropManager.rollChestDrops(
      { continentId, depth: depthRatio },
      chestType
    );

    return {
      gold: treasureDrops.gold || Math.floor(100 + depthRatio * 400),
      materials: treasureDrops.items || [],
    };
  }, [dropManager]);

  /**
   * 素材をプレイヤーデータに追加
   * @param {Array} materials - 追加する素材配列 [{ type, count }]
   */
  const addMaterials = useCallback((materials) => {
    if (!materials || materials.length === 0) return;

    updatePlayerData?.((prev) => {
      const currentMaterials = prev.twinKey?.materials || {};
      const updatedMaterials = { ...currentMaterials };

      for (const mat of materials) {
        if (mat && mat.type) {
          updatedMaterials[mat.type] = (updatedMaterials[mat.type] || 0) + (mat.count || 1);
        }
      }

      return {
        ...prev,
        twinKey: {
          ...prev.twinKey,
          materials: updatedMaterials,
        },
      };
    });
  }, [updatePlayerData]);

  /**
   * ゴールドをプレイヤーデータに追加
   * @param {number} amount - 追加するゴールド量
   */
  const addGold = useCallback((amount) => {
    if (!amount || amount <= 0) return;

    updatePlayerData?.((prev) => {
      const currentGold = prev.twinKey?.gold || 0;
      return {
        ...prev,
        twinKey: {
          ...prev.twinKey,
          gold: currentGold + amount,
        },
      };
    });
  }, [updatePlayerData]);

  /**
   * 現在の所持素材を取得
   */
  const materials = useMemo(() => {
    return playerData?.twinKey?.materials || {};
  }, [playerData]);

  /**
   * 現在のゴールドを取得
   */
  const gold = useMemo(() => {
    return playerData?.twinKey?.gold || 0;
  }, [playerData]);

  /**
   * ドロップ統計を取得
   */
  const dropStats = useMemo(() => {
    return dropManager.getSessionStats();
  }, [dropManager]);

  return {
    // 計算
    calculateBattleRewards,
    calculateTreasureRewards,

    // 追加
    addMaterials,
    addGold,

    // 状態
    materials,
    gold,
    dropStats,

    // マネージャー
    dropManager,
  };
}

export default useDrops;
